import { TooltipContent } from '@design-system';
import { getLtcPool } from '@shared/calculations/funding/projection/projectionTotals';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import type { StrategyPresentation } from './types';

const pct = (rate: number | null | undefined, digits = 2) =>
  rate != null ? `${(rate * 100).toFixed(digits)}%` : null;

function guaranteedRateLabel(terms: ClaimTerms): string {
  const initial = pct(terms.accumulatedValueGuaranteedInitialRate);
  const renewal = pct(terms.accumulatedValueGuaranteedRenewalRate);
  const initialYears = terms.accumulatedValueGuaranteedInitialRateYears;
  if (initial && renewal && initialYears != null) {
    return `${initial} for ${initialYears} years, then ${renewal}`;
  }
  return 'the guaranteed crediting schedule';
}

function riderFactorLabel(terms: ClaimTerms, policyYear: number): string {
  const rate = terms.inflationRate ?? 0;
  if (rate <= 0) return '1.00 (no LTC inflation rider)';

  const rawYears = Math.max(Math.floor(policyYear) - 1, 0);
  const capYears = terms.inflationCapYears > 0 ? terms.inflationCapYears : null;
  const years = capYears != null ? Math.min(rawYears, capYears) : rawYears;
  const rateLabel = pct(rate) ?? 'rider rate';
  const factor =
    terms.inflationIsSimple === true
      ? `1 + ${rateLabel} × ${years}`
      : `(1 + ${rateLabel})^${years}`;
  return capYears != null && rawYears > capYears
    ? `${factor} (capped at ${capYears} years)`
    : factor;
}

function ForecareContractValueTooltip({
  terms,
  startingValue,
  result,
}: {
  terms: ClaimTerms;
  startingValue: number;
  result: number;
}) {
  const charge = pct(terms.monthlyContinuationBenefitChargeRate, 3);
  return (
    <TooltipContent className="max-w-sm text-xs">
      <div className="mb-1 font-semibold">Contract Value before care</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">At issue</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">This policy year</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        <span className="text-gray-300">Guaranteed rate</span>
        <span className="font-semibold">{guaranteedRateLabel(terms)}</span>
        {charge && (
          <>
            <span className="text-gray-300">Monthly LTC rider charge</span>
            <span className="font-semibold">{charge}</span>
          </>
        )}
        <span className="text-gray-300">Monthly step</span>
        <span className="font-semibold">
          max(prior value, (prior value - rider charge) × (1 + guaranteed monthly rate))
        </span>
      </div>
      <div className="mt-2 text-[10px] text-gray-300">
        Before care, rider charges apply monthly and the policy guarantee prevents a lower month-end
        Contract Value.
      </div>
    </TooltipContent>
  );
}

function ForecareTotalLtcTooltip({
  terms,
  row,
  startingValue,
  hasContractValue,
}: {
  terms: ClaimTerms;
  row: AccumulationYear;
  startingValue: number;
  hasContractValue: boolean;
}) {
  if (!hasContractValue) {
    return (
      <TooltipContent className="max-w-sm text-xs">
        <div className="mb-1 font-semibold">Total LTC dollars before care</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">At issue</span>
          <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
          <span className="text-gray-300">This policy year</span>
          <span className="font-semibold tabular-nums">
            {currency(row.snapshot.theoreticalLtcPoolCeiling)}
          </span>
          <span className="text-gray-300">Formula</span>
          <span className="font-semibold">
            at-issue Total LTC Benefit × LTC inflation rider factor
          </span>
          <span className="text-gray-300">Factor</span>
          <span className="font-semibold">{riderFactorLabel(terms, row.policyYear)}</span>
        </div>
      </TooltipContent>
    );
  }

  return (
    <TooltipContent className="max-w-sm text-xs">
      <div className="mb-1 font-semibold">Total LTC dollars before care</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">At issue total</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">Contract Value</span>
        <span className="font-semibold tabular-nums">
          {currency(row.snapshot.effectiveFaceAmount)}
        </span>
        <span className="text-gray-300">Maximum Extended Benefit</span>
        <span className="font-semibold tabular-nums">
          {currency(row.snapshot.effectiveContinuationBenefit)}
        </span>
        <span className="text-gray-300">Total this year</span>
        <span className="font-semibold tabular-nums">
          {currency(row.snapshot.theoreticalLtcPoolCeiling)}
        </span>
        <span className="text-gray-300">Formula</span>
        <span className="font-semibold">Contract Value + Maximum Extended Benefit</span>
        <span className="text-gray-300">Maximum Extended Benefit factor</span>
        <span className="font-semibold">{riderFactorLabel(terms, row.policyYear)}</span>
      </div>
      <div className="mt-2 text-[10px] text-gray-300">
        Contract Value follows the guaranteed rate and monthly rider charge schedule. Maximum
        Extended Benefit follows the LTC inflation rider when elected.
      </div>
    </TooltipContent>
  );
}

function ForecareMonthlyBenefitTooltip({
  terms,
  row,
  startingValue,
}: {
  terms: ClaimTerms;
  row: AccumulationYear;
  startingValue: number;
}) {
  return (
    <TooltipContent className="max-w-sm text-xs">
      <div className="mb-1 font-semibold">Monthly benefit before care</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">At issue</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">This policy year</span>
        <span className="font-semibold tabular-nums">
          {currency(row.snapshot.monthlyBenefitCap)}
        </span>
        <span className="text-gray-300">Formula</span>
        <span className="font-semibold">at-issue monthly benefit × LTC inflation rider factor</span>
        <span className="text-gray-300">Factor</span>
        <span className="font-semibold">{riderFactorLabel(terms, row.policyYear)}</span>
      </div>
    </TooltipContent>
  );
}

// ForeCare accumulation: the headline is the annuity Contract Value growing on
// its guaranteed crediting rate (year-1 rate then renewal), alongside the Total
// LTC Benefit pool and the monthly maximum. In guaranteed mode the year-snapshot
// stores the Contract Value in `effectiveFaceAmount` and the Total LTC Benefit
// in `theoreticalLtcPoolCeiling`; the legacy fallback has no contract value
// (effectiveFaceAmount = 0) and we drop that column.
function buildForecareAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  const hasContractValue = timeline.some(r => r.snapshot.effectiveFaceAmount > 0.5);
  if (hasContractValue) {
    const atIssueCv =
      terms.accumulatedValueAtIssue ?? timeline[0]?.snapshot.effectiveFaceAmount ?? 0;
    columns.push({
      key: 'contract-value',
      label: 'Contract Value',
      tooltip: (
        <HeaderTooltip
          description="Before care begins, Contract Value grows from the at-issue value using the guaranteed crediting schedule and monthly LTC rider charge."
          formulas={[
            {
              label: 'Monthly step',
              expression:
                'max(prior value, (prior value - rider charge) × (1 + guaranteed monthly rate))',
            },
            { label: 'Rider charge', expression: 'prior value × monthly LTC rider charge' },
          ]}
          notes={`Guaranteed rate: ${guaranteedRateLabel(terms)}.`}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={
            <ForecareContractValueTooltip
              terms={terms}
              startingValue={atIssueCv}
              result={row.snapshot.effectiveFaceAmount}
            />
          }
        />
      ),
    });
  }

  const atIssuePool = timeline[0]?.snapshot.theoreticalLtcPoolCeiling ?? 0;
  if (timeline.some(r => r.snapshot.theoreticalLtcPoolCeiling > 0.5)) {
    columns.push({
      key: 'ltc-pool',
      label: 'Total LTC dollars',
      tooltip: (
        <HeaderTooltip
          description={
            hasContractValue
              ? 'Before care begins, total LTC dollars equal scheduled Contract Value plus Maximum Extended Benefit.'
              : "Before care begins, total LTC dollars are the policy's Total LTC Benefit pool."
          }
          formulas={
            hasContractValue
              ? [
                  {
                    label: 'Total LTC dollars',
                    expression: 'Contract Value + Maximum Extended Benefit',
                  },
                  {
                    label: 'Maximum Extended Benefit',
                    expression: 'at-issue Maximum Extended Benefit × LTC inflation rider factor',
                  },
                ]
              : [
                  {
                    label: 'Total LTC dollars',
                    expression: 'at-issue Total LTC Benefit × LTC inflation rider factor',
                  },
                ]
          }
          notes={
            hasContractValue
              ? 'The LTC inflation rider grows Maximum Extended Benefit when elected; otherwise it stays flat.'
              : 'The LTC inflation rider grows the Total LTC Benefit pool when elected; otherwise it stays flat.'
          }
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.theoreticalLtcPoolCeiling}
          prevValue={prev?.snapshot.theoreticalLtcPoolCeiling ?? null}
          tooltip={
            <ForecareTotalLtcTooltip
              terms={terms}
              row={row}
              startingValue={atIssuePool}
              hasContractValue={hasContractValue}
            />
          }
        />
      ),
    });
  }

  if (timeline.some(r => r.snapshot.monthlyBenefitCap > 0.5)) {
    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="Before care begins, monthly benefit is the policy's monthly LTC maximum for that policy year."
          formulas={[
            {
              label: 'Monthly benefit',
              expression: 'at-issue monthly benefit × LTC inflation rider factor',
            },
          ]}
          notes="Flat when no LTC inflation rider is elected; capped at the rider cap year when one applies."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={
            <ForecareMonthlyBenefitTooltip
              terms={terms}
              row={row}
              startingValue={baseMonthly}
            />
          }
        />
      ),
    });
  }

  return columns;
}

// Global Atlantic ForeCare + the generic `annuityHybrid` fallback. New
// guaranteed ForeCare rows use a two-layer Contract Value / Extended Benefit
// ledger; incomplete or generic rows keep the old single-pool baseline. All
// values are read off precomputed month state/deltas — the presentation never
// re-derives strategy math.

interface ForecareLikeDeltas {
  contractValueInterestCredit?: number;
  contractValueRiderCharge?: number;
  acceleratedBenefitDraw?: number;
  extendedBenefitDraw?: number;
}

function isGuaranteedForecareTerms(terms: ClaimTerms): boolean {
  return (
    terms.baseBenefitGuaranteedInitialRate != null &&
    terms.baseBenefitGuaranteedInitialRateYears != null &&
    terms.baseBenefitGuaranteedRenewalRate != null &&
    terms.monthlyContinuationBenefitChargeRate != null
  );
}

export const forecarePresentation: StrategyPresentation = {
  accumulationColumns: buildForecareAccumulationColumns,

  // "Pool remaining" cell — guaranteed rows show Contract Value and Maximum
  // Extended Benefit layers; fallback rows show the legacy single pool.
  poolCellTooltip: ({ month, openState }) => {
    const sd = month.deltas.strategyDeltas as ForecareLikeDeltas;
    const hasGuaranteedDeltas =
      sd.contractValueInterestCredit != null ||
      sd.contractValueRiderCharge != null ||
      sd.acceleratedBenefitDraw != null ||
      sd.extendedBenefitDraw != null;

    if (hasGuaranteedDeltas && openState.pools.extensionOfBenefitsPool > 0) {
      return (
        <BalanceBreakdownTooltip
          title="ForeCare guaranteed LTC layers"
          description="Guaranteed ForeCare draws Accelerated Benefits from Contract Value first, credits guaranteed interest on remaining Contract Value, waives rider charges during LTC withdrawal months, then draws Maximum Extended Benefit."
          sections={[
            {
              heading: 'Contract Value / Accelerated Benefit',
              opening: openState.pools.baseBenefitPool,
              closing: month.state.pools.baseBenefitPool,
              lines: [
                {
                  label: 'Guaranteed interest credit',
                  value: sd.contractValueInterestCredit ?? 0,
                  kind: 'add',
                },
                {
                  label: 'LTC rider charge',
                  value: sd.contractValueRiderCharge ?? 0,
                  kind: 'subtract',
                },
                {
                  label: 'Accelerated benefit draw',
                  value: sd.acceleratedBenefitDraw ?? 0,
                  kind: 'subtract',
                },
              ],
            },
            {
              heading: 'Maximum Extended Benefit',
              opening: openState.pools.extensionOfBenefitsPool,
              closing: month.state.pools.extensionOfBenefitsPool,
              lines: [
                {
                  label: 'Extended benefit draw',
                  value: sd.extendedBenefitDraw ?? 0,
                  kind: 'subtract',
                },
              ],
              note: 'Flat in the no-inflation-rider guaranteed illustration.',
            },
          ]}
          footer="Rider charges cannot reduce no-claim month-end Contract Value below the prior month-end value."
        />
      );
    }

    const opening = getLtcPool(openState);
    const closing = getLtcPool(month.state);
    const accrual = month.deltas.base.inflationAccrual + month.deltas.eob.inflationAccrual;
    const drawn = month.deltas.base.depletion + month.deltas.eob.depletion;

    if (opening === 0 && closing === 0 && accrual === 0 && drawn === 0) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC coverage pool"
        description="ForeCare reimburses care from a single Total LTC Benefit pool. Unused daily benefit stays in the pool; with the LTC inflation rider elected, the remaining balance grows at each policy anniversary."
        sections={[
          {
            opening,
            closing,
            lines: [
              { label: 'Inflation accrual', value: accrual, kind: 'add' },
              { label: 'Drawn this month', value: drawn, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  // "Death benefit" cell — the residual Contract Value floor. LTC draws reduce
  // the Contract Value first; the unused remainder is the non-LTC payout.
  deathBenefitCellTooltip: ({ month, openState, terms }) => {
    if (terms.minimumGuaranteedDeathBenefit <= 0) return null;
    const opening = openState.pools.deathBenefit;
    const closing = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    if (opening === 0 && closing === 0 && reduction === 0) return null;
    const sd = month.deltas.strategyDeltas as ForecareLikeDeltas;

    if (isGuaranteedForecareTerms(terms)) {
      return (
        <BalanceBreakdownTooltip
          title="Residual Contract Value"
          description="The residual non-LTC payout is the remaining Contract Value. Accelerated LTC withdrawals reduce it; guaranteed interest can increase the remaining value during claim."
          sections={[
            {
              opening,
              closing,
              lines: [
                {
                  label: 'Guaranteed interest credit',
                  value: sd.contractValueInterestCredit ?? 0,
                  kind: 'add',
                },
                {
                  label: 'LTC rider charge',
                  value: sd.contractValueRiderCharge ?? 0,
                  kind: 'subtract',
                },
                {
                  label: 'Accelerated benefit draw',
                  value: sd.acceleratedBenefitDraw ?? 0,
                  kind: 'subtract',
                },
              ],
            },
          ]}
        />
      );
    }

    return (
      <BalanceBreakdownTooltip
        title="Residual Contract Value"
        description="ForeCare's Contract Value is reduced by LTC withdrawals (paid first as Accelerated Benefits). Any Contract Value remaining after the claim is paid as a residual non-LTC payout."
        sections={[
          {
            opening,
            closing,
            lines: [{ label: 'Reduced by LTC draw', value: reduction, kind: 'subtract' }],
          },
        ]}
      />
    );
  },

  // "Projected payout" cell — reimbursement (or indemnity) waterfall. Single
  // pool, so there is no AOB/COB split line.
  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // Keep the tooltip alive on a fully-ineligible month (waiting period / <2
    // ADL) when room & board is still out-of-pocket — Independent Living books
    // R&B even on non-paying months (cmTwo guards the same way); it's the exact
    // OOP this modeling exists to surface. Otherwise PaidCell shows the
    // zero-row reason natively.
    if (eligibleDays === 0 && (month.facts.roomAndBoardCost ?? 0) === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawn = month.deltas.base.depletion + month.deltas.eob.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;

    const isIndemnity = terms.isIndemnity;
    const poolExhausted = getLtcPool(month.state) === 0;
    const eligibleUnpaid = Math.max(eligibleCost - paid, 0);
    // Total out of pocket = care shortfall (cost − paid) + out-of-pocket room &
    // board, which for Independent Living coexists with paying care days rather
    // than being a non-paying-day bucket.
    const roomAndBoard = month.facts.roomAndBoardCost ?? 0;
    const totalOop = Math.max(cost - paid, 0) + roomAndBoard;
    const breakdown = month.events.ineligibleDayBreakdown;
    const oopLt2Adl = breakdown.lt2AdlDays * dailyCost;
    const oopWp = breakdown.inWpDays * dailyCost;
    const oopPrePolicy = breakdown.prePolicyDays * dailyCost;

    const mode = isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = isIndemnity
      ? 'Pays a fixed cash benefit up to the monthly cap regardless of actual cost, drawn from the LTC pool. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.'
      : 'Reimburses eligible incurred cost, up to the monthly cap, drawn from the LTC pool.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {isIndemnity ? (
            // Indemnity is a cash waterfall, not a day-bucket breakdown — see
            // `indemnityPayoutWaterfall`.
            indemnityPayoutWaterfall(month)
          ) : (
            <>
              <span className="text-gray-300">Care cost</span>
              <span className="font-semibold tabular-nums">{currency(cost)}</span>
              {isPartial && (
                <>
                  <span className="text-gray-300">
                    Eligible ({eligibleDays}/{DAYS_IN_MONTH} days)
                  </span>
                  <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
                </>
              )}
              <span className="text-gray-300">Policy paid</span>
              <span className="font-semibold tabular-nums">−{currency(paid)}</span>
              <span className="text-gray-300">Out of pocket</span>
              <span className="font-semibold tabular-nums text-rose-300">{currency(totalOop)}</span>
              {eligibleUnpaid > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    {poolExhausted ? '· pool exhausted' : '· over monthly cap'}
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(eligibleUnpaid)}</span>
                </>
              )}
              {oopWp > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · waiting period <span className="text-gray-500">· {breakdown.inWpDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopWp)}</span>
                </>
              )}
              {oopLt2Adl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    {'· <2 ADL'} <span className="text-gray-500">· {breakdown.lt2AdlDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopLt2Adl)}</span>
                </>
              )}
              {roomAndBoard > 0 && (
                <>
                  <span className="pl-2 text-gray-400">· room &amp; board</span>
                  <span className="tabular-nums text-rose-300">{currency(roomAndBoard)}</span>
                </>
              )}
              {oopPrePolicy > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · before policy start
                    <span className="text-gray-500"> · {breakdown.prePolicyDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopPrePolicy)}</span>
                </>
              )}
            </>
          )}
        </div>
        <div className="mt-1 text-[10px] text-gray-400">Drawn from LTC pool {currency(drawn)}</div>
      </TooltipContent>
    );
  },

  // Single coverage pool — no theoretical "pool limit" ceiling column (the
  // remaining balance is the only meaningful number once any dollar is drawn).
  hiddenColumns: ['pool-limit'],

  // Single coverage pool — drop the AOB/COB framing in the Drawn cell.
  drawnCellLabels: {
    description:
      'Cumulative dollars drawn from the LTC coverage pool. ' +
      'Drawn = Σ (monthly pool depletion).',
    baseLabel: 'Coverage pool drawn',
    singlePool: true,
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC coverage pool balance, with this month's change beneath. ForeCare reimburses from a single Total LTC Benefit pool; with the LTC inflation rider the remaining balance grows at each policy anniversary."
        formulas={[
          { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
          {
            label: 'Inflation accrual',
            expression: 'opening × inflation rate (at each policy anniversary)',
          },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly maximum, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly maximum, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period days and days the insured fails the 2-ADL trigger."
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="Residual Contract Value — the annuity's account value, reduced by LTC withdrawals. Any amount remaining after the claim is paid as a non-LTC payout."
        formulas={[{ label: 'Residual', expression: 'max(0, Contract Value − LTC drawn)' }]}
        notes="Once cumulative LTC withdrawals exceed the Contract Value, the residual is $0."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this policy year, with the daily equivalent beneath."
        formulas={[
          {
            label: 'Monthly max',
            expression: (
              <>
                at-issue daily × days in month ×{' '}
                <GrowthFactor
                  rate="rider inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
        ]}
        notes="ForeCare's sample illustration does not elect the LTC inflation rider, so the cap is flat. Capped at the rider's inflation cap year when elected."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative dollars drawn from the LTC coverage pool through this month."
        formulas={[{ label: 'Drawn', expression: 'Σ (monthly pool depletion)' }]}
      />
    ),
  },
};
